import React, { useCallback, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { getExtras } from 'src/reducers/common/listReducer';

import { Label } from 'src/jbc-front/components/Form';

import { CLIENT_ALLOWANCE_FORM } from 'src/constants/FormNames';
import { getAllowanceFormValues } from 'src/reducers/clients/allowances';
import selector from 'src/utils/Utils';
import { getRadioOptions } from 'src/reducers/selectOptions';
import { getErrors } from 'src/utils/Motivation';

import EditableRow from './EditableRow';
import TargetSettings from '../../../components/TargetSettings';

import MotivationStepButton from './MotivationStepButton';
import MotivationPaymentMonth from './MotivationPaymentMonth';
import LeavesTable from './LeavesTable';

const motivationStepLabel = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItem: 'center',
  width: '100%',
  paddingBottom: '4px'
};

const MotivationSteppedBonus = ({
  selectedMotivationStep,
  selectedProbationStep,
  selectedMotivationState,
  selectedErrorTables,
  dispatch
}) => {
  const { t } = useTranslation();
  const selectedProbationSteps = selectedProbationStep || [{ fromMonth: '1', toMonth: '', bonusAmount: '' }];
  const editableData = selectedMotivationState === 'fulltime' ? selectedMotivationStep : selectedProbationSteps;
  const isMotivationOrProbation = selectedMotivationState === 'fulltime' ? 'motivationStep' : 'probationStep';
  const [errorRows, setErrorRows] = useState(getErrors(selectedErrorTables.errorTables));
  const MAX_ROWS = 9;

  const disableAddRowButton = selectedMotivationStep.length - 1 === 9;

  useEffect(
    () => {
      setErrorRows(getErrors(selectedErrorTables.errorTables));
    },
    [selectedErrorTables]
  );

  const handleInputChanged = useCallback(
    (event, rowIndex) => {
      const { name, value } = event.target;
      const list = [...editableData];
      list[rowIndex][name] = value;
      dispatch(change(CLIENT_ALLOWANCE_FORM, isMotivationOrProbation, list));
    },
    [editableData]
  );

  const handleRemoveRow = useCallback(
    deleteRowIndex => {
      const list = [...editableData];
      list.splice(deleteRowIndex, 1);
      dispatch(change(CLIENT_ALLOWANCE_FORM, isMotivationOrProbation, list));
    },
    [editableData]
  );

  const handleAddNewRow = useCallback(
    insertionRowIndex => {
      const { toMonth } = editableData[insertionRowIndex] || 0;
      const nextMonth = parseInt(toMonth || 0, 10) + 1;
      const fromMonth = nextMonth > 999 ? 999 : nextMonth;
      if (editableData.length > MAX_ROWS) return;
      const newRows = [
        ...editableData.slice(0, insertionRowIndex + 1),
        {
          fromMonth: fromMonth.toString(),
          toMonth: nextMonth > 999 ? '999' : nextMonth.toString(),
          bonusAmount: null
        },
        ...editableData.slice(insertionRowIndex + 1)
      ];
      dispatch(change(CLIENT_ALLOWANCE_FORM, isMotivationOrProbation, newRows));
    },
    [editableData]
  );

  return (
    <>
      <div>
        <Label text={t('features.ClientMotivation.components.MonthlySteppedBonus.first_effective')} required />
        <MotivationPaymentMonth />
      </div>
      <div style={motivationStepLabel}>
        <Label
          note={<div>{t('features.ClientAllowances.components.MotivationSteppedBonus.motivation_step_notice')}</div>}
          text={t('features.ClientMotivation.global.motivation_bonus_setting')}
          className="motivation-label"
        />
        <MotivationStepButton />
      </div>
      <div className="editable-table">
        <div className="editable-table-header">
          <div className="editable-table-row">
            <div className="editable-table-column">{t('features.ClientMotivation.components.AllowanceMotivation.step')}</div>
            <div className="text-center editable-table-column">
              {t('features.ClientMotivation.components.AllowanceMotivation.month_period')}
            </div>
            <div className="text-center editable-table-column">{t('features.ClientMotivation.components.AllowanceMotivation.amount')}</div>
            {selectedMotivationState === 'fulltime' && <div className="text-center editable-table-column" />}
          </div>
        </div>
        <div className="editable-table-body">
          {editableData.map((row, index) => (
            <EditableRow
              setErrorRows={setErrorRows}
              disable={disableAddRowButton}
              errorRows={errorRows}
              handleRemoveRow={handleRemoveRow}
              handleInputChanged={handleInputChanged}
              handleAddNewRow={handleAddNewRow}
              key={`row-list-${index + 1}`}
              index={index}
              row={row}
            />
          ))}
        </div>
      </div>

      <LeavesTable />
      <div style={{ marginTop: '1.5rem' }}>
        <TargetSettings allowanceType="payment" />
      </div>
    </>
  );
};

const mapStateToProps = state => ({
  leaveTypes: getRadioOptions(state, 'leaveTypes'),
  selectedMotivationStep: selector(getAllowanceFormValues(state), 'motivationStep'),
  selectedClientAllowanceItemIds: selector(getAllowanceFormValues(state), 'clientAllowanceItemIds'),
  selectedProbationStep: selector(getAllowanceFormValues(state), 'probationStep'),
  selectedMotivationState: selector(getAllowanceFormValues(state), 'motivationState'),
  selectedErrorTables: getExtras('clientAllowances', state, 'errorTables', null)
});

export default connect(mapStateToProps)(MotivationSteppedBonus);
